import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  MarkerType,
  Controls,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  deleteBranchHierarchy,
  deleteSequenceByCategoriesId,
  deleteStepHierarchy,
  getSequenceById,
} from "../../../services/CollectionsSvc";
import ViewPollBox from "../../poll/viewPollBox";
import { Leva, useControls } from "leva";
import getLayoutedElements from "../../useAutoLayout";
import { Button } from "react-bootstrap";
import ViewFormBox from "../../form/ViewFormBox";
import BuildSequence from "./BuildSequence";
import ViewCallToActionBox from "../content/ViewCallToActionBox";
import ViewProductBox from "../content/ViewProductBox";
import ViewSignUpBox from "../content/ViewSignUpBox";

const proOptions = {
  account: "paid-pro",
  hideAttribution: true,
};

function createDynamicEdges(ids, edgeVotes) {
  const edges = [];

  const nodes = ids.map(({ id }) => {
    const [type, from, to] = id.split("-");
    return { id, type, from, to };
  });

  let lastStep = null;

  nodes.forEach((node) => {
    if (node.type === "step") {
      const childBranches = nodes.filter(
        (n) => n.type === "branch" && n.from === node.from
      );
      childBranches.forEach((child) => {
        edges.push({
          source: node.id,
          target: child.id,
          id: node.id + child.id,
          label: edgeVotes[node.id + "-step-" + child.to] || "",
        });
      });

      if (lastStep && lastStep.from !== node.from) {
        edges.push({
          source: lastStep.id,
          target: node.id,
          id: lastStep.id + node.id,
          sourceHandle: "right-handle",
          targetHandle: "left-handle",
          edgeType: "stepToStep",
          label: edgeVotes[lastStep.id + "-" + node.id] || "",
        });
      }
      lastStep = node;
    }

    if (node.type === "branch") {
      const childBranches = nodes.filter(
        (n) => n.type === "branch" && n.from === node.to
      );
      childBranches.forEach((child) => {
        edges.push({
          source: node.id,
          target: child.id,
          id: child.id + node.id,
          label: edgeVotes["step-" + child.from + "-step-" + child.to] || "",
        });
      });
    }
  });

  edges.forEach((edge) => {
    edge.type = "smoothstep";
    edge.style = {
      stroke: "gray",
      strokeWidth: 2,
    };
    edge.markerEnd = {
      type: MarkerType.ArrowClosed,
      width: 20,
      height: 20,
      color: "gray",
    };
    edge.labelStyle = {
      fontSize: "12px",
      fill: "black",
    };
  });

  return edges;
}

function calculateEdgeVotes(steps, currentEdgeVotes) {
  const updatedEdgeVotes = { ...currentEdgeVotes };

  const processStep = (step, index, parentSteps) => {
    const debateData = step.part?.debate;

    if (debateData?.sides) {
      const totalVotes = steps[0]?.part?.debate?.votes || 0;

      // Handle branch options
      debateData.sides.forEach((side) => {
        const branch = step.branches?.find((b) => b.sideId === side.id);

        if (branch?.nextStep?.id) {
          const nextStepId = branch.nextStep.id;
          const edgeId = `step-${step.id}-step-${nextStepId}`;
          if (!updatedEdgeVotes[edgeId]) {
            updatedEdgeVotes[edgeId] = 0;
          }
          const percentage =
            totalVotes > 0 ? ((side.votes || 0) / totalVotes) * 100 : 0;
          updatedEdgeVotes[edgeId] = percentage.toFixed(2) + "%";

          // Recursively process the next step in the branch
          processStep(branch.nextStep, index + 1, [...parentSteps, step]);
        }
      });

      // Handle non-branch options (next step in order)
      if (index < steps.length - 1) {
        const nextStepId = steps[index + 1].id;
        const edgeId = `step-${step.id}-step-${nextStepId}`;
        if (!updatedEdgeVotes[edgeId]) {
          updatedEdgeVotes[edgeId] = 0;
        }
        const nonBranchSides = debateData.sides.filter(
          (side) => !step.branches?.some((branch) => branch.sideId === side.id)
        );

        const nonBranchVotes = nonBranchSides.reduce(
          (sum, side) => sum + (side.votes || 0),
          0
        );
        const percentage =
          totalVotes > 0 ? (nonBranchVotes / totalVotes) * 100 : 0;
        updatedEdgeVotes[edgeId] = percentage.toFixed(2) + "%";
      }
    }
  };

  steps.forEach((step, index) => {
    processStep(step, index, []);
  });

  return updatedEdgeVotes;
}

function createReactFlowData(steps, edgeVotes) {
  const nodes = [];
  // Ensure steps are sorted by `order` to create the proper sequence
  const data = [...steps].sort((a, b) => a.order - b.order);

  data.forEach((step, index) => {
    // Add the main step node
    const positions = { x: 0, y: 0 };
    nodes.push({
      id: `step-${step.id}`,
      data: {
        ...step,
        stepIndex: index,
      },
      type: "customNode", // Always "customNode"
      position: positions, // Dynamic positioning
    });

    // Add branch nodes and connect them
    if (step.branches && step.branches.length > 0) {
      let counter = 1;
      const nodesForBranches = (step, count) => {
        step.branches.forEach((branch, branchIndex) => {
          const branchId = `branch-${step?.id}-${branch?.nextStep?.id}`;
          nodes.push({
            id: branchId,
            data: {
              branchId: branch.id,
              ...branch,
              ...branch.nextStep,
              stepIndex: index,
            },
            type: "customNode", // Always "customNode"
            position: {
              x: 0,
              y: 0,
            },
          });

          // If the branch specifies a next step, connect it
          if (branch.nextStep?.branches?.length > 0) {
            counter = counter + 1;
            nodesForBranches(branch.nextStep, counter);
          }
        });
      };

      nodesForBranches(step, counter, true);
    }
  });

  const edging = createDynamicEdges(nodes, edgeVotes);

  return { nodes, edges: edging };
}

const CollectionBranches = ({ sequenceId, selNetworkId, networks }) => {
  const [isAddStepBranchModalVisible, setIsAddStepBranchModalVisible] =
    useState(false);
  const nodeRefs = useRef({});
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [edgeVotes, setEdgeVotes] = useState({});

  const layoutOptions = useControls({
    algorithm: {
      value: "dagre",
      options: ["dagre"],
    },
    direction: {
      value: "TB",
      options: {
        down: "TB",
        right: "LR",
        up: "BT",
        left: "RL",
      },
    },
    spacing: [50, 50],
  });

  const [isAddStepModalVisible, setIsAddStepModalVisible] = useState(false);
  const [selectedStep, setSelectedStep] = useState(false);
  const [collectionData, setCollectionData] = useState({});
  const [sequType, setSequType] = useState("Survey");

  const onAddBranch = (stepBranchDetails) => {
    if (!collectionData.hasAnyUserVoted) {
      setIsAddStepBranchModalVisible(true);
      setSelectedStep(stepBranchDetails);
    } else {
      alert(
        "Editing the sequence structure is disabled because users voted for the sequence."
      );
    }
  };

  const onNewStep = () => {
    if (collectionData?.sequenceCategories?.length > 0) return;
    if (!collectionData.hasAnyUserVoted) {
      if (collectionData?.type == "Quiz") {
        setSequType("Quiz");
      }
      setIsAddStepModalVisible(true);
    } else {
      alert(
        "Editing the sequence structure is disabled because users voted for the sequence."
      );
    }
  };

  const onAddFirstStep = (seqType) => {
    if (seqType == "Quiz") {
      setSequType("Quiz");
    }
    setIsAddStepModalVisible(true);
  };

  const fetchCollection = useCallback(async () => {
    try {
      const response = await getSequenceById(sequenceId);
      if (response.data.data) {
        const sequence = response.data.data;

        setCollectionData(sequence);
      }
    } catch (error) {
      console.error("Failed to fetch sequence data:", error);
    }
  }, [sequenceId]);

  const onDeleteNode = useCallback(
    async (id, nodeType) => {
      if (collectionData?.sequenceCategories?.length > 0) return;
      if (collectionData.hasAnyUserVoted) {
        alert(
          "Editing the sequence structure is disabled because users voted for the sequence."
        );
        return;
      }

      try {
        if (
          window.confirm(
            `Are you sure you want to delete this ${nodeType}? Deleting it will remove all its branches and sub-branches.`
          )
        ) {
          const response =
            nodeType === "step"
              ? await deleteStepHierarchy(id)
              : await deleteBranchHierarchy(id);
          if (String(response.data.status) === "1") {
            fetchCollection();
          }
        }
      } catch (error) {
        console.error("Failed to delete node:", error);
      }
    },
    [fetchCollection, collectionData.hasAnyUserVoted]
  );

  const onEditNode = useCallback(
    async (refresh) => {
      if (collectionData?.sequenceCategories?.length > 0) return;
      if (refresh) {
        fetchCollection();
      }
    },
    [fetchCollection]
  );

  const nodeTypes = useMemo(
    () => ({
      customNode: (props) => {
        let gPartType = props?.data?.part.partType.toLowerCase();
        if (
          gPartType === "poll" ||
          gPartType === "question" ||
          gPartType === "quiz"
        ) {
          return (
            <ViewPollBox
              {...props}
              totalSteps={collectionData?.steps?.length}
              onAddBranch={onAddBranch}
              // hideBranchOption={collectionData.type === "Quiz"}
              onNewStep={onNewStep}
              onDelete={onDeleteNode}
              steps={collectionData?.steps}
              hasVotes={collectionData.hasAnyUserVoted}
              onEditContent={onEditNode}
              selNetworkId={selNetworkId}
            />
          );
        } else if (gPartType === "cta") {
          return (
            <ViewCallToActionBox
              {...props}
              totalSteps={collectionData?.steps?.length}
              onAddBranch={onAddBranch}
              onNewStep={onNewStep}
              onDelete={onDeleteNode}
              steps={collectionData?.steps}
              onEditContent={onEditNode}
            />
          );
        } else if (gPartType === "product") {
          return (
            <ViewProductBox
              {...props}
              totalSteps={collectionData?.steps?.length}
              onAddBranch={onAddBranch}
              onNewStep={onNewStep}
              onDelete={onDeleteNode}
              steps={collectionData?.steps}
              onEditContent={onEditNode}
            />
          );
        } else if (gPartType === "sign-up") {
          return (
            <ViewSignUpBox
              {...props}
              totalSteps={collectionData?.steps?.length}
              onAddBranch={onAddBranch}
              onNewStep={onNewStep}
              onDelete={onDeleteNode}
              steps={collectionData?.steps}
              onEditContent={onEditNode}
              selNetworkId={selNetworkId}
            />
          );
        } else {
          return (
            <ViewFormBox
              {...props}
              totalSteps={collectionData?.steps?.length}
              onAddBranch={onAddBranch}
              onNewStep={onNewStep}
              onDelete={onDeleteNode}
              steps={collectionData?.steps}
              onEditContent={onEditNode}
              selNetworkId={selNetworkId}
            />
          );
        }
      },
    }),
    [collectionData.steps, collectionData.type, onDeleteNode]
  );

  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  useEffect(() => {
    if (collectionData.steps) {
      const updatedEdgeVotes = calculateEdgeVotes(
        collectionData.steps,
        edgeVotes
      );
      setEdgeVotes(updatedEdgeVotes);

      const rawData = createReactFlowData(
        collectionData.steps,
        updatedEdgeVotes
      );
      setNodes(rawData.nodes);
      setEdges(rawData.edges);
    }
  }, [collectionData.steps]);

  const updateSequence = () => {
    if (collectionData?.hasAnyUserVoted) {
      alert(
        "Editing the sequence structure is disabled because users voted for the sequence."
      );
      return;
    }
    const payload = {
      sequenceId: parseInt(sequenceId),
    };
    deleteSequenceByCategoriesId(payload)
      .then(() => {
        alert("Sequence saved successfully!");
        fetchCollection();
      })
      .catch((error) => {
        console.error("Failed to save sequence:", error);
        alert("Failed to save sequence. Please try again.");
      });
  };

  const reactFlowInstance = useRef(null); // Reference to the React Flow instance

  // Update state when memoized data changes
  useEffect(() => {
    if (nodes.length > 0) {
      if (reactFlowInstance.current) {
        // reactFlowInstance.current?.fitView(); // Automatically zooms out to fit the whole graph
      }
    }
  }, [nodes]);

  const onInit = (instance) => {
    reactFlowInstance.current = instance; // Store the React Flow instance
  };

  const autoLayoutNodes = useMemo(() => {
    const { nodes: layoutedNodes } = getLayoutedElements(
      nodes,
      edges,
      nodeRefs || {}
    );
    return {
      nodes: layoutedNodes,
      edges,
    };
  }, [nodes, edges]);

  return (
    <>
      <Leva hidden />
      {collectionData?.sequenceCategories?.length > 0 ? (
        <>
          <div className="alert alert-warning mt-3">
            You can’t build a step-by-step flow because you’ve selected
            categories. In this case, the sequence will automatically choose
            questions based on the selected categories.
          </div>
          <span onClick={updateSequence} className="customLabel remove-button">
            Remove categories
          </span>
        </>
      ) : collectionData?.steps?.length > 0 ? (
        <>
          {collectionData.hasAnyUserVoted && (
            <div className="alert alert-warning mt-3">
              Editing the sequence structure is disabled because users voted for
              the sequence.
            </div>
          )}

          <div style={{ height: "60vh", width: "100%" }}>
            <ReactFlow
              nodes={autoLayoutNodes.nodes}
              edges={autoLayoutNodes.edges}
              preventScrolling={false}
              nodeTypes={nodeTypes} // Register the custom node type here
              fitView
              // onNodesChange={onInit}
              // onEdgesChange={onInit}
              onInit={onInit}
              fitViewOptions={{
                padding: 0.2,
              }}
              style={{ background: "#ffffff" }}
              proOptions={proOptions}
              readOnly={collectionData.hasAnyUserVoted}
            >
              <Controls />
            </ReactFlow>

            {nodes?.map((node) => (
              <div
                key={node.id}
                ref={(el) => (nodeRefs.current[node.id] = el)}
                style={{
                  visibility: "hidden",
                  // display: "flex",
                  width: "max-content",
                  // height: "max-content",
                }}
              >
                {node.data?.part?.debate ? (
                  <ViewPollBox
                    hideBranchOption={collectionData.type === "Quiz"}
                    totalSteps={collectionData?.steps?.length}
                    data={node.data}
                  />
                ) : (
                  <ViewFormBox
                    totalSteps={collectionData?.steps?.length}
                    data={node.data}
                  />
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button onClick={() => onAddFirstStep(collectionData.type)}>
            Add First Step
          </Button>
        </div>
      )}

      {isAddStepBranchModalVisible && (
        <BuildSequence
          show={isAddStepBranchModalVisible}
          onClose={(isSuccess) => {
            if (isSuccess) {
              fetchCollection();
            }
            setIsAddStepBranchModalVisible(false);
            setSelectedStep({});
          }}
          orderNumber={collectionData?.steps?.length || 0}
          sequenceId={parseInt(sequenceId)}
          selNetworkId={selNetworkId}
          selectedStep={selectedStep}
          isFromAddBranch={true}
        />
      )}
      {isAddStepModalVisible && (
        <BuildSequence
          sequenceDetails={collectionData}
          show={isAddStepModalVisible}
          onClose={(isSuccess) => {
            if (isSuccess) {
              fetchCollection();
            }
            setIsAddStepModalVisible(false);
          }}
          orderNumber={collectionData?.steps?.length || 0}
          sequenceId={parseInt(sequenceId)}
          selNetworkId={selNetworkId}
          sequenceType={sequType}
          isFromAddBranch={false}
          networks={networks}
        />
      )}
    </>
  );
};

const ReactFlowCollectionBranches = (props) => {
  return (
    <ReactFlowProvider>
      <CollectionBranches {...props} />
    </ReactFlowProvider>
  );
};
export default ReactFlowCollectionBranches;
